import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { scriptPlayer } from "../services/scriptPlayerService";

export const useScriptPlayer = (onAudioLevel: (volume: number) => void) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  // 일시정지 상태 관리
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playbackRate, setRate] = useState(1.0);
  const [progress, setProgress] = useState({
    currentTime: 0,
    duration: 0,
    maxTimeReached: 0,
  });

  useEffect(() => {
    // 립싱크 데이터 연결
    scriptPlayer.onAudioLevel = (volume) => onAudioLevel(volume);

    scriptPlayer.onPlayEnd = () => {
      setIsPlaying(false);
      setIsPaused(false);
    };

    scriptPlayer.onError = () => {
      setError(t("audioLoadFailed"));
      setIsPlaying(false);
    };

    scriptPlayer.onProgress = (currentTime, duration, maxTimeReached) => {
      setProgress({ currentTime, duration, maxTimeReached });
    };

    return () => scriptPlayer.stop();
  }, [onAudioLevel, t]);

  const playAudio = useCallback(async (url: string) => {
    setError(null); // 재생 시도 시 이전 에러 초기화
    setIsPlaying(true);
    setIsPaused(false);
    setProgress({ currentTime: 0, duration: 0, maxTimeReached: 0 });
    await scriptPlayer.playAudio(url);
  }, []);

  // 토글 함수 (일시정지)
  const togglePause = useCallback(() => {
    const pausedState = scriptPlayer.togglePause();
    setIsPaused(pausedState);
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    scriptPlayer.setPlaybackRate(rate);
    setRate(rate);
  }, []);

  const seekTo = useCallback((time: number) => {
    scriptPlayer.seekTo(time);
  }, []);

  // 재생을 중단하고 상태를 초기화 (브리핑 건너뛰기 등에 사용)
  const stop = useCallback(() => {
    scriptPlayer.stop();
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  return {
    isPlaying,
    isPaused,
    playbackRate,
    progress,
    error,
    playAudio,
    togglePause,
    setPlaybackRate,
    seekTo,
    stop,
  };
};
